import { useQuery } from "@apollo/client";
import Edit from "@mui/icons-material/Edit";
import SwapHoriz from "@mui/icons-material/SwapHoriz";
import {
  Alert,
  Box,
  Button,
  Chip,
  Grid,
  Link,
  Paper,
  Skeleton,
  Stack,
  Typography,
  useTheme,
} from "@mui/material";
import { mdHeight, xsHeight } from "components/AppBar";
import { InfoIconTooltip } from "components/InfoIconTooltip";
import { FeatureFlagKey } from "encamp-shared/src/featureFlags";
import { GetWasteContainerQuery, ReconcileChoice, UnitType } from "generated-graphql/graphql";
import { useBreadcrumb } from "hooks/useBreadcrumbs";
import { useFeatureFlags } from "hooks/useFeatureFlags";
import { useTenant } from "hooks/useTenant";
import { ReactNode, useState } from "react";
import { Link as RouterLink, useParams } from "react-router-dom";

import { classificationLabels } from "../Overview/Tiles/Charts/constants";
import { SHIPMENT_STATUS_LABELS } from "../Shipments/shipmentDisplay";
import { AccumulationClockSection } from "./AccumulationClockSection";
import { GET_WASTE_CONTAINER_QUERY } from "./api";
import { ConsolidateContainersDialog } from "./ConsolidateContainersDialog";
import {
  containerTypeLabel,
  NO_VALUE_PLACEHOLDER,
  formatContainerDate,
  formatQuantity,
  formatReportablePounds,
  reportablePounds,
  STATUS_TRANSITION_OPTIONS,
} from "./containerDisplay";
import { ContainerFillCard } from "./ContainerFillCard";
import { ContainerFillEventDialog } from "./ContainerFillEventDialog";
import { ContainerHistorySection } from "./ContainerHistorySection";
import { SectionCard } from "./containerSections";
import { EmptyContainerDialog } from "./EmptyContainerDialog";
import { FillHistorySection } from "./FillHistorySection";
import { ContainerLabelSection } from "./Label/ContainerLabelSection";
import { MoveContainerDialog } from "./MoveContainerDialog";
import { SetContainerStatusDialog } from "./SetContainerStatusDialog";
import { WasteContainerDialog } from "./WasteContainerDialog";
import { WasteContainerStatusChip } from "./WasteContainerStatusChip";

type WasteContainerDetailData = NonNullable<GetWasteContainerQuery["wasteContainer"]>;
type ContainerShipmentRefData = NonNullable<WasteContainerDetailData["shippedOnShipment"]>;

function yesNo(value: boolean): string {
  return value ? "Yes" : "No";
}

function FieldValue({
  label,
  children,
  secondary,
}: {
  label: string;
  children: ReactNode;
  /** Extra content below the main value — e.g. the reportable-pounds hint under an amount. */
  secondary?: ReactNode;
}) {
  return (
    <Grid size={{ xs: 12, sm: 6, md: 4 }}>
      <Typography variant="caption" color="text.secondary" display="block">
        {label}
      </Typography>
      {/* `div`, not the default `p`: values include block-level children — the
          classification Chip renders a div, and a div inside a p is invalid
          HTML that React reports as a hydration error. */}
      <Typography variant="body2" component="div">
        {children}
      </Typography>
      {secondary}
    </Grid>
  );
}

/**
 * The shipment that carried this container.
 *
 * `tenantId` can be absent while the tenant is still resolving, and a link
 * built without it points at `/o/undefined/...`. The number is still worth
 * showing then, just not as something to click.
 */
function ShipmentLink({
  shipment,
  tenantId,
}: {
  shipment: ContainerShipmentRefData | null | undefined;
  tenantId: string | undefined;
}) {
  if (!shipment) {
    return NO_VALUE_PLACEHOLDER;
  }
  if (!tenantId) {
    return shipment.manifestRequestNumber;
  }
  return (
    <Link component={RouterLink} to={`/o/${tenantId}/waste/shipments/${shipment.id}`}>
      {shipment.manifestRequestNumber}
    </Link>
  );
}

/**
 * The official manifest for that shipment.
 *
 * The tracking number comes from the shipment's own snapshot, so it survives
 * the window where the pipeline has deleted the manifest row and not yet
 * rebuilt it. Without an id there is nothing to open, so it renders as text —
 * the same rule the shipment page's rail follows.
 */
function ManifestLink({
  shipment,
  tenantId,
}: {
  shipment: ContainerShipmentRefData | null | undefined;
  tenantId: string | undefined;
}) {
  if (!shipment?.manifestTrackingNumber) {
    return NO_VALUE_PLACEHOLDER;
  }
  if (!shipment.processedManifestId || !tenantId) {
    return shipment.manifestTrackingNumber;
  }
  return (
    <Link
      component={RouterLink}
      to={`/o/${tenantId}/waste/manifests/${shipment.processedManifestId}`}
    >
      {shipment.manifestTrackingNumber}
    </Link>
  );
}

/**
 * "≈ 1,500 lb" under a mass amount; a muted "specific gravity needed" hint
 * under a volume amount (toReportable can't convert volume to weight without
 * a density, and specificGravity is always null until Project 2 lands stream
 * density). Renders nothing when there's no amount to annotate.
 */
function ReportableAmountHint({
  amount,
  unit,
}: {
  amount: number | null | undefined;
  unit: UnitType | null | undefined;
}) {
  if (amount == null || !unit) {
    return null;
  }
  const lb = reportablePounds(amount, unit);
  if (lb !== null) {
    return (
      <Typography variant="caption" color="text.secondary" display="block">
        {formatReportablePounds(lb)}
      </Typography>
    );
  }
  return (
    <Stack direction="row" alignItems="center" spacing={0.25}>
      <Typography variant="caption" color="text.secondary" fontStyle="italic">
        Specific gravity needed to compute pounds
      </Typography>
      <InfoIconTooltip tooltip="This amount is recorded by volume. Once the waste stream has a specific gravity, this container's amount will also show in pounds." />
    </Stack>
  );
}

/**
 * Who this container is and what is in it.
 *
 * The label and where it sits identify the drum; the waste stream is the title,
 * because what a container holds is what a reader came for. Matches the waste
 * specs' container header.
 */
function ContainerIdentity({
  container,
  isArchived,
}: {
  container: WasteContainerDetailData;
  isArchived: boolean;
}) {
  return (
    <Box sx={{ minWidth: 260 }}>
      <Stack direction="row" alignItems="baseline" spacing={1.5} useFlexGap flexWrap="wrap">
        <Typography variant="h6" component="p" sx={{ fontWeight: 600 }}>
          {container.containerLabel}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          {[
            // Size and kind read as one phrase, the way the specs write it:
            // "55 gal steel drum".
            [
              container.capacity != null
                ? formatQuantity(container.capacity, container.capacityUnit)
                : null,
              containerTypeLabel(container.containerType),
            ]
              .filter(Boolean)
              .join(" "),
            container.facility?.name,
            container.accumulationArea?.name,
          ]
            .filter(Boolean)
            .join(" • ")}
        </Typography>
      </Stack>
      {/* The waste is what the container is FOR, so it carries the title
        and the label above it identifies which drum. */}
      <Typography variant="h5" component="h1" sx={{ mt: 0.5 }}>
        {container.wasteStream?.name ?? "Uncharacterized"}
      </Typography>
      <Stack
        direction="row"
        alignItems="center"
        spacing={1}
        useFlexGap
        flexWrap="wrap"
        sx={{ mt: 1.25 }}
      >
        <WasteContainerStatusChip status={container.status} />
        {container.wasteStream?.classification && (
          <Chip
            label={classificationLabels[container.wasteStream.classification]}
            size="small"
            variant="outlined"
          />
        )}
        {isArchived && <Chip label="Archived" size="small" variant="outlined" />}
      </Stack>
    </Box>
  );
}

/** What the waste is — paired with the label card on its grid row, and kept
 * out of the container's own facts below, which describe the vessel rather
 * than its contents. */
function WasteClassificationCard({
  container,
  tenantId,
  showStreamLink,
}: {
  container: WasteContainerDetailData;
  tenantId: string | undefined;
  showStreamLink: boolean;
}) {
  return (
    <SectionCard title="Waste &amp; classification">
      <Grid container spacing={2}>
        {/*
         * The stream link goes to its detail drawer, which is where the waste
         * profile lives. It matters here because the streams grid filters on
         * shipment lines, so it does not list a stream that has never
         * shipped. For a stream that only exists for container tracking, this
         * is the only route to its profile.
         *
         * Only a link when manifest-views-v2 is on. The streams route is
         * gated on that flag with `fallback="../"`, and this page is gated on
         * container-tracking, so a tenant with one flag and not the other
         * would click through and land on the waste overview.
         */}
        <FieldValue label="Waste stream">
          {container.wasteStream && showStreamLink && (
            <Link
              component={RouterLink}
              to={`/o/${tenantId}/waste/streams/${container.wasteStream.id}`}
            >
              {container.wasteStream.name}
            </Link>
          )}
          {container.wasteStream && !showStreamLink && container.wasteStream.name}
          {!container.wasteStream && NO_VALUE_PLACEHOLDER}
        </FieldValue>
        {/* This detail page is only reachable for container-tracking tenants
        (the wasteContainer query is ContainerTracking-gated), so the
        reportability field renders without an extra flag check. */}
        <FieldValue label="Counts toward generation">
          {yesNo(container.countsTowardGeneration)}
        </FieldValue>
        <FieldValue label="Classification">
          {container.wasteStream?.classification ? (
            <Chip
              label={classificationLabels[container.wasteStream.classification]}
              size="small"
              variant="outlined"
            />
          ) : (
            NO_VALUE_PLACEHOLDER
          )}
        </FieldValue>
      </Grid>
    </SectionCard>
  );
}

/** Where the waste went. Renders nothing for a container that has never been
 * on a shipment — a card of empty rows says that at length. Canceled
 * shipments do not count: `openShipments` excludes them, and a canceled one
 * carried nothing. */
function ShipmentRecordSection({
  container,
  tenantId,
}: {
  container: WasteContainerDetailData;
  tenantId: string | undefined;
}) {
  const hasShipment = container.openShipments.length > 0 || container.shippedOnShipment != null;
  if (!hasShipment) {
    return null;
  }
  return (
    <Grid container spacing={3}>
      <Grid size={{ xs: 12, md: 6 }}>
        <SectionCard title="Shipping &amp; manifest">
          <Grid container spacing={2}>
            {/* A container awaiting pickup is on a shipment that has not left
                yet, and the estimated pickup date it shows comes from that
                shipment — so there has to be a way back to it. */}
            <FieldValue label="On shipment">
              {container.openShipments.length > 0
                ? container.openShipments.map((shipment, index) => (
                    <Box component="span" key={shipment.id}>
                      {index > 0 && ", "}
                      <ShipmentLink shipment={shipment} tenantId={tenantId} />
                      <Typography component="span" variant="body2" color="text.secondary">
                        {` (${SHIPMENT_STATUS_LABELS[shipment.status]})`}
                      </Typography>
                    </Box>
                  ))
                : NO_VALUE_PLACEHOLDER}
            </FieldValue>
            <FieldValue label="Shipped on">
              <ShipmentLink shipment={container.shippedOnShipment} tenantId={tenantId} />
            </FieldValue>
            <FieldValue label="Manifest">
              <ManifestLink shipment={container.shippedOnShipment} tenantId={tenantId} />
            </FieldValue>
            <FieldValue label="Shipped date">
              {formatContainerDate(container.shippedDate)}
            </FieldValue>
            <FieldValue label="Estimated pickup date">
              {formatContainerDate(container.estimatedPickupDate)}
            </FieldValue>
          </Grid>
        </SectionCard>
      </Grid>
    </Grid>
  );
}

/** The paired half-width section cards between the header and the dialogs:
 * clock/fill, the two histories, and the label summary (half width like the
 * rest -- its 4x6 preview reads as a thumbnail, not a page-wide banner)
 * beside what the waste actually is. */
function ContainerSectionGrids({
  container,
  tenantId,
  showStreamLink,
}: {
  container: WasteContainerDetailData;
  tenantId: string | undefined;
  showStreamLink: boolean;
}) {
  return (
    <>
      <Grid container spacing={3}>
        <Grid size={{ xs: 12, md: 6 }}>
          <AccumulationClockSection container={container} />
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <ContainerFillCard container={container} />
        </Grid>
      </Grid>

      <Grid container spacing={3} alignItems="flex-start">
        <Grid size={{ xs: 12, md: 6 }}>
          <FillHistorySection container={container} />
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <ContainerHistorySection container={container} />
        </Grid>
      </Grid>

      <Grid container spacing={3} alignItems="stretch">
        <Grid size={{ xs: 12, md: 6 }}>
          <ContainerLabelSection container={container} />
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <WasteClassificationCard
            container={container}
            tenantId={tenantId}
            showStreamLink={showStreamLink}
          />
        </Grid>
      </Grid>
    </>
  );
}

function ContainerDetailContent({ container }: { container: WasteContainerDetailData }) {
  const theme = useTheme();
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [moveDialogOpen, setMoveDialogOpen] = useState(false);
  const [statusDialogOpen, setStatusDialogOpen] = useState(false);
  const [emptyDialogOpen, setEmptyDialogOpen] = useState(false);
  const [consolidateDialogOpen, setConsolidateDialogOpen] = useState(false);
  const [reconcileFillDialogOpen, setReconcileFillDialogOpen] = useState(false);
  const { tenantId } = useTenant();
  const { featureFlags } = useFeatureFlags();
  const showStreamLink = !!featureFlags[FeatureFlagKey.ManifestViewsV2];
  const isArchived = !!container.archivedAt;
  const hasStatusTransitions = STATUS_TRANSITION_OPTIONS[container.status].length > 0;

  return (
    <Stack
      spacing={3}
      sx={{
        px: 3,
        py: 2,
        // The sections are cards; on a white page they had nothing to sit on.
        backgroundColor: "canvas",
        minHeight: {
          xs: `calc(100vh - ${theme.spacing(xsHeight)})`,
          md: `calc(100vh - ${theme.spacing(mdHeight)})`,
        },
      }}
    >
      {/* space-between alone leaves no minimum gap, so the status chips sit
          flush against the first action button before the row is narrow enough
          to wrap. The explicit spacing guarantees the gap and the wrap gives the
          actions their own line instead of compressing them. */}
      {/* Identity, then what it is, then what you can do to it -- each on its
          own line, the order the waste specs put them in. */}
      <Stack spacing={2}>
        <ContainerIdentity container={container} isArchived={isArchived} />
        {!isArchived && (
          <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap">
            <Button
              variant="outlined"
              size="small"
              startIcon={<SwapHoriz />}
              onClick={() => setMoveDialogOpen(true)}
            >
              Move
            </Button>
            {container.isPermanent && (
              <Button variant="outlined" size="small" onClick={() => setEmptyDialogOpen(true)}>
                Empty tank
              </Button>
            )}
            <Button variant="outlined" size="small" onClick={() => setConsolidateDialogOpen(true)}>
              Consolidate
            </Button>
            {hasStatusTransitions && (
              <Button variant="outlined" size="small" onClick={() => setStatusDialogOpen(true)}>
                Change status
              </Button>
            )}
            <Button
              variant="contained"
              size="small"
              startIcon={<Edit />}
              onClick={() => setEditDialogOpen(true)}
            >
              Edit
            </Button>
          </Stack>
        )}
      </Stack>

      {isArchived && (
        <Alert severity="info" variant="outlined">
          This container was archived on {formatContainerDate(container.archivedAt)} and is
          read-only.
          {container.shippedOnShipment && (
            <>
              {" It left the site on "}
              <ShipmentLink shipment={container.shippedOnShipment} tenantId={tenantId} />.
            </>
          )}
        </Alert>
      )}

      <ContainerSectionGrids
        container={container}
        tenantId={tenantId}
        showStreamLink={showStreamLink}
      />

      {editDialogOpen && (
        <WasteContainerDialog container={container} onClose={() => setEditDialogOpen(false)} />
      )}
      <MoveContainerDialog
        container={container}
        open={moveDialogOpen}
        onClose={() => setMoveDialogOpen(false)}
      />
      {statusDialogOpen && (
        <SetContainerStatusDialog
          container={container}
          onClose={() => setStatusDialogOpen(false)}
          onReconcileFollowUp={(choice) => {
            if (choice === ReconcileChoice.AddEvent) {
              setReconcileFillDialogOpen(true);
            } else if (choice === ReconcileChoice.AdjustCapacity) {
              setEditDialogOpen(true);
            }
          }}
        />
      )}
      {emptyDialogOpen && (
        <EmptyContainerDialog container={container} onClose={() => setEmptyDialogOpen(false)} />
      )}
      {consolidateDialogOpen && (
        <ConsolidateContainersDialog
          container={container}
          onClose={() => setConsolidateDialogOpen(false)}
        />
      )}
      {reconcileFillDialogOpen && (
        <ContainerFillEventDialog
          container={container}
          onClose={() => setReconcileFillDialogOpen(false)}
        />
      )}

      <ShipmentRecordSection container={container} tenantId={tenantId} />

      <SectionCard title="Details">
        <Grid container spacing={2}>
          <FieldValue label="Facility">
            {container.facility?.name ? (
              <Link
                component={RouterLink}
                to={`/o/${tenantId}/waste/facilities/${container.facilityId}`}
              >
                {container.facility.name}
              </Link>
            ) : (
              NO_VALUE_PLACEHOLDER
            )}
          </FieldValue>
          <FieldValue label="Container type">
            {containerTypeLabel(container.containerType)}
          </FieldValue>
          <FieldValue label="Capacity">
            {formatQuantity(container.capacity, container.capacityUnit)}
          </FieldValue>
          <FieldValue
            label="Amount on hand"
            secondary={
              <ReportableAmountHint amount={container.amount} unit={container.amountUnit} />
            }
          >
            {formatQuantity(container.amount, container.amountUnit)}
            {container.isEstimate && container.amount != null ? " (estimate)" : ""}
          </FieldValue>
          <FieldValue label="Accumulation area">
            {container.accumulationArea?.name ?? NO_VALUE_PLACEHOLDER}
          </FieldValue>
          <FieldValue label="Accumulation start date">
            {formatContainerDate(container.accumulationStartDate)}
          </FieldValue>
          <FieldValue label="Moved to central date">
            {formatContainerDate(container.movedToCentralDate)}
          </FieldValue>
          <FieldValue label="Permanent vessel (tank)">{yesNo(container.isPermanent)}</FieldValue>
          <FieldValue label="Flow-through">{yesNo(container.isFlowThrough)}</FieldValue>
          <FieldValue label="Amounts are estimates">{yesNo(container.isEstimate)}</FieldValue>
          <FieldValue label="SQG shipping 200+ miles">
            {yesNo(container.sqgOver200Miles)}
          </FieldValue>
        </Grid>
      </SectionCard>
    </Stack>
  );
}

/**
 * The page's shape before its data arrives.
 *
 * Mirrors the real layout -- same canvas, same header rows, same two-by-two
 * card grid -- so the content resolves in place instead of jumping when it
 * lands.
 */
function ContainerDetailSkeleton() {
  const theme = useTheme();
  const card = (height: number) => (
    <Paper elevation={0} sx={{ p: 3, height: "100%" }}>
      <Skeleton variant="text" width={120} height={16} sx={{ mb: 2 }} />
      <Skeleton variant="rectangular" height={height} />
    </Paper>
  );
  return (
    <Stack
      spacing={3}
      sx={{
        px: 3,
        py: 2,
        backgroundColor: "canvas",
        minHeight: {
          xs: `calc(100vh - ${theme.spacing(xsHeight)})`,
          md: `calc(100vh - ${theme.spacing(mdHeight)})`,
        },
      }}
    >
      <Stack spacing={2}>
        <Box>
          <Skeleton variant="text" width={320} height={20} />
          <Skeleton variant="text" width={240} height={34} sx={{ mt: 0.5 }} />
          <Stack direction="row" spacing={1} sx={{ mt: 1.25 }}>
            <Skeleton variant="rounded" width={92} height={24} />
            <Skeleton variant="rounded" width={78} height={24} />
          </Stack>
        </Box>
        <Stack direction="row" spacing={1}>
          {[86, 108, 124, 72].map((w) => (
            <Skeleton key={w} variant="rounded" width={w} height={32} />
          ))}
        </Stack>
      </Stack>

      <Grid container spacing={3}>
        <Grid size={{ xs: 12, md: 6 }}>{card(148)}</Grid>
        <Grid size={{ xs: 12, md: 6 }}>{card(148)}</Grid>
      </Grid>
      <Grid container spacing={3} alignItems="flex-start">
        <Grid size={{ xs: 12, md: 6 }}>{card(180)}</Grid>
        <Grid size={{ xs: 12, md: 6 }}>{card(180)}</Grid>
      </Grid>
      {card(80)}
      {card(220)}
    </Stack>
  );
}

export function WasteContainerDetail() {
  const { containerId } = useParams<{ containerId: string }>();
  const { tenantId } = useTenant();

  const { data, loading, error } = useQuery(GET_WASTE_CONTAINER_QUERY, {
    variables: { id: containerId ?? "" },
    skip: !containerId,
  });
  const container = data?.wasteContainer;

  useBreadcrumb([
    {
      label: "Waste",
      to: tenantId ? `/o/${tenantId}/waste` : undefined,
    },
    {
      label: "Containers",
      to: tenantId ? `/o/${tenantId}/waste/containers` : undefined,
    },
    // Nothing until the label is known -- see the shipment page.
    ...(container ? [{ label: container.containerLabel }] : []),
  ]);

  if (loading && !container) {
    return <ContainerDetailSkeleton />;
  }

  if (error || !container) {
    return (
      <Stack spacing={1} sx={{ px: 3, py: 4 }} alignItems="center">
        <Typography variant="h6">Container not found</Typography>
        <Typography variant="body2" color="text.secondary">
          The container may have been archived, or the link may be incorrect.
        </Typography>
      </Stack>
    );
  }

  return <ContainerDetailContent container={container} />;
}
